import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  QuestionMarkCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  BookOpenIcon,
  ChevronDoubleRightIcon,
  TrashIcon,
  PlusCircleIcon,
  FlagIcon,
} from "@heroicons/react/24/solid";
import {
  getQuizzesByChapterId,
  deleteQuizfromChapter,
} from "../../State/Quiz/Action";

const QuizListWithAnswers = () => {
  const { chapterId } = useParams();
  // Extract moduleId from query string (e.g., ?moduleId=abc123)
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const moduleId = queryParams.get("moduleId");
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const chapterQuizData = useSelector(
    (state) => state.quiz?.quizListResponse?.data
  );

  const deleteQuizResult = useSelector(
    (state) => state.quiz?.deleteQuizResult.data
  );
  const errorDelete = useSelector((state) => state.quiz?.errorDelete?.message);
  const loadingDelete = useSelector((state) => state.quiz?.loadingDelete);

  const [localDeleteSuccess, setLocalDeleteSuccess] = useState(false);
  const [localDeleteError, setLocalDeleteError] = useState(null);

  useEffect(() => {
    dispatch(getQuizzesByChapterId(chapterId));
  }, [dispatch, chapterId]);

  useEffect(() => {
    if (deleteQuizResult?.success) {
      setLocalDeleteSuccess(true);
      setLocalDeleteError(null);
      dispatch(getQuizzesByChapterId(chapterId));
    } else if (errorDelete) {
      setLocalDeleteError(errorDelete);
    }
  }, [deleteQuizResult, errorDelete, dispatch, chapterId]);

  const isCorrect = (flag) => flag === true;

  const handleDelete = async (quizId) => {
    if (window.confirm("Are you sure you want to delete this question?")) {
      dispatch(deleteQuizfromChapter(quizId, chapterId));
    }
  };

  const handleAddMore = () => {
    navigate(`/add-quiz/${chapterId}?moduleId=${moduleId}`);
  };

  const handleFinish = () => {
    // Get courseId from the current URL
    const courseId = queryParams.get('courseId');
    if (courseId) {
      navigate(`/add-new/${moduleId}?courseId=${courseId}`);
    } else {
      navigate(`/add-new/${moduleId}`);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-purple-200 p-6 pb-24">
      {/* Alerts */}
      {localDeleteSuccess && (
        <div className="max-w-2xl mx-auto mb-4 text-green-700 bg-green-100 border border-green-300 p-3 rounded">
          Question deleted successfully!
        </div>
      )}
      {localDeleteError && (
        <div className="max-w-2xl mx-auto mb-4 text-red-700 bg-red-100 border border-red-300 p-3 rounded">
          Error deleting question: {localDeleteError}
        </div>
      )}

      {chapterQuizData ? (
        <div className="max-w-4xl mx-auto bg-white shadow-xl rounded-lg p-6">
          <div className="mb-6 text-center">
            <h2 className="text-3xl font-bold text-indigo-800 flex items-center justify-center gap-2">
              <BookOpenIcon className="h-8 w-8 text-indigo-500" />
              {chapterQuizData.chapterTitle}
            </h2>
            <p className="text-gray-600">
              {chapterQuizData.chapterDescription}
            </p>
          </div>

          {chapterQuizData.quizzes.length === 0 && (
            <div className="text-center text-gray-500 italic mb-6">
              No quizzes found for this chapter.
            </div>
          )}

          {chapterQuizData.quizzes.map((quiz, index) => (
            <div
              key={quiz.id}
              className="mb-8 p-4 border-l-4 border-indigo-500 bg-indigo-50 rounded-lg shadow relative"
            >
              <button
                onClick={() => handleDelete(quiz.id)}
                disabled={loadingDelete}
                className="absolute top-2 right-2 text-red-500 hover:text-red-700 disabled:opacity-50"
                title="Delete this question"
              >
                <TrashIcon className="h-6 w-6" />
              </button>

              <div className="flex items-start gap-2 mb-2">
                <QuestionMarkCircleIcon className="h-6 w-6 text-indigo-700 mt-1" />
                <h3 className="text-lg font-semibold text-indigo-900">
                  Q{index + 1}: {quiz.question}
                </h3>
              </div>

              <ul className="ml-8 mt-3 space-y-2">
                {["A", "B", "C", "D", "E"].map((opt) => {
                  const optionValue = quiz[`option${opt}`];
                  const correctFlag = quiz[`option${opt}Correct`];
                  if (!optionValue) return null;

                  return (
                    <li
                      key={opt}
                      className={`flex items-center gap-2 p-2 rounded-md ${
                        isCorrect(correctFlag)
                          ? "bg-green-100 text-green-800 font-medium border border-green-300"
                          : "bg-white border border-gray-200"
                      }`}
                    >
                      {isCorrect(correctFlag) ? (
                        <CheckCircleIcon className="h-5 w-5 text-green-600" />
                      ) : (
                        <XCircleIcon className="h-5 w-5 text-gray-400" />
                      )}
                      <span>{`${opt}. ${optionValue}`}</span>
                    </li>
                  );
                })}
              </ul>

              {quiz.explanation && (
                <div className="mt-4 flex items-start gap-2 bg-yellow-50 border-l-4 border-yellow-400 p-3 rounded-md">
                  <LightBulbIcon className="h-5 w-5 text-yellow-600 mt-1" />
                  <p className="text-yellow-800">
                    <strong>Explanation:</strong> {quiz.explanation}
                  </p>
                </div>
              )}

              <div className="mt-2 text-right text-sm text-indigo-600 italic">
                {quiz.multipleAnswers
                  ? "Multiple correct answers"
                  : "Single correct answer"}
              </div>
            </div>
          ))}

          {/* Action Buttons */}
          <div className="mt-10 flex justify-between">
            <button
              onClick={handleAddMore}
              className="bg-blue-600 text-white font-semibold px-4 py-2 rounded-md flex items-center gap-2 hover:bg-blue-700"
            >
              <PlusCircleIcon className="h-5 w-5" />
              Add More Questions
            </button>
            <button
              onClick={handleFinish}
              className="bg-green-600 text-white font-semibold px-4 py-2 rounded-md flex items-center gap-2 hover:bg-green-700"
            >
              <FlagIcon className="h-5 w-5" />
              Finish Quiz
            </button>
          </div>
        </div>
      ) : (
        <div className="text-center text-lg text-gray-600 mt-12">
          <ChevronDoubleRightIcon className="h-6 w-6 inline-block animate-bounce text-indigo-500 mr-2" />
          Loading quiz data...
        </div>
      )}
    </div>
  );
};

export default QuizListWithAnswers;
